import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import {
  ChatBackendMessageDto,
  ChatConversation,
  ChatConversationDetailDto,
  ChatConversationListItemDto,
  ChatMessage,
  ChatSendRequest,
  ChatStreamEvent,
  ChatUsageResponse,
  RenameConversationRequest,
} from '../models/chat.models';

const MAX_HISTORY_TURNS = 20;
const AUTO_TITLE_MAX_CHARS = 50;
const MANUAL_TITLE_MAX_CHARS = 80;
// Backend caps pageSize at 50; we ask for the max in a single shot until the
// sidebar grows infinite scroll. TODO: paginate when users start hitting this.
const LIST_PAGE_SIZE = 50;

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly conversationsSubject = new BehaviorSubject<ChatConversation[]>([]);
  readonly conversations$: Observable<ChatConversation[]> = this.conversationsSubject.asObservable();

  private readonly activeConversationIdSubject = new BehaviorSubject<string | null>(null);
  readonly activeConversationId$: Observable<string | null> = this.activeConversationIdSubject.asObservable();

  /** Single source of truth for everything the user has touched this session. */
  private readonly cache = new Map<string, ChatConversation>();

  /** Ids the backend has confirmed exist (returned by GET /conversations or
   * implicitly persisted after the first POST /messages on a local-only id). */
  private readonly serverKnownIds = new Set<string>();

  /** Ids whose full message list has already been pulled from GET /{id}.
   * Local-only conversations are intentionally absent from this set — they
   * have nothing to fetch yet. */
  private readonly messagesLoadedIds = new Set<string>();

  /** Active agent filter applied to conversations$. null = general (CAFI). */
  private currentAgentCode: string | null = null;

  private readonly conversationsBaseUrl = `${environment.cafler_backoffice_back_url}/chat/conversations`;
  private readonly usageUrl = `${environment.cafler_backoffice_back_url}/chat/usage`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
  ) {
    this.loadConversationsFromServer();
  }

  // ---------------------------------------------------------------------------
  // Public surface — preserves the contract the chat-page/sidebar already use.
  // ---------------------------------------------------------------------------

  switchStorageContext(agentCode: string | null): void {
    this.currentAgentCode = agentCode;
    this.activeConversationIdSubject.next(null);
    this.emitConversations();
  }

  getActiveAgentCode(): string | null {
    return this.currentAgentCode;
  }

  /** Synchronous read used by chat-page to render the active turn. The cache
   * may not yet have messages loaded; setActive() triggers the lazy fetch. */
  getConversation(id: string): ChatConversation | null {
    return this.cache.get(id) ?? null;
  }

  createConversation(title: string, agentCode?: string): ChatConversation {
    const conversation: ChatConversation = {
      id: this.randomId(),
      title,
      updatedAt: new Date().toISOString(),
      messages: [],
      titleAutoGenerated: true,
      agentCode: agentCode ?? this.currentAgentCode ?? undefined,
    };
    this.cache.set(conversation.id, conversation);
    // Local-only until the first POST /messages — backend creates it implicitly
    // via EnsureConversationAsync at that point.
    this.emitConversations();
    this.activeConversationIdSubject.next(conversation.id);
    return conversation;
  }

  setActive(conversationId: string): void {
    this.activeConversationIdSubject.next(conversationId);
    this.maybeFetchMessages(conversationId);
  }

  deleteConversation(conversationId: string): void {
    const previous = this.cache.get(conversationId);
    if (!previous) return;
    // Capture before the optimistic mutation — we need to know whether to call
    // the API after we've already cleared the local Set.
    const wasServerKnown = this.serverKnownIds.has(conversationId);

    // Optimistic: remove locally, repoint active to the next conversation.
    this.cache.delete(conversationId);
    this.serverKnownIds.delete(conversationId);
    this.messagesLoadedIds.delete(conversationId);
    this.emitConversations();
    if (this.activeConversationIdSubject.value === conversationId) {
      const next = this.visibleSortedList()[0]?.id ?? null;
      this.activeConversationIdSubject.next(next);
    }

    // Local-only conversations were never persisted: nothing to call.
    if (!wasServerKnown) return;

    this.http
      .delete<void>(`${this.conversationsBaseUrl}/${conversationId}`)
      .pipe(take(1), catchError((err: unknown) => {
        const status = this.statusOf(err);
        // 404 means another tab/session already deleted it — local state is
        // already correct, treat as success.
        if (status !== 404) {
          // Network or 5xx: reinstate the conversation so the UI doesn't lie.
          this.cache.set(conversationId, previous);
          this.serverKnownIds.add(conversationId);
          this.emitConversations();
          // eslint-disable-next-line no-console
          console.warn('[ChatService] deleteConversation failed, reverted', err);
        }
        return of(void 0);
      }))
      .subscribe();
  }

  clearAllConversations(): void {
    const snapshot = Array.from(this.cache.values());
    const knownIdsSnapshot = new Set(this.serverKnownIds);
    const messagesLoadedSnapshot = new Set(this.messagesLoadedIds);

    this.cache.clear();
    this.serverKnownIds.clear();
    this.messagesLoadedIds.clear();
    this.emitConversations();
    this.activeConversationIdSubject.next(null);

    this.http
      .delete<void>(this.conversationsBaseUrl)
      .pipe(take(1), catchError((err: unknown) => {
        // Restore the local snapshot and reload from server to converge.
        for (const c of snapshot) this.cache.set(c.id, c);
        for (const id of knownIdsSnapshot) this.serverKnownIds.add(id);
        for (const id of messagesLoadedSnapshot) this.messagesLoadedIds.add(id);
        this.emitConversations();
        // eslint-disable-next-line no-console
        console.warn('[ChatService] clearAllConversations failed, reloading', err);
        this.loadConversationsFromServer();
        return of(void 0);
      }))
      .subscribe();
  }

  /** Manual rename from the sidebar. Locks <c>titleAutoGenerated</c> so future
   * auto-title calls don't overwrite the user's choice. */
  renameConversation(conversationId: string, rawTitle: string): void {
    const trimmed = rawTitle.trim();
    if (trimmed.length === 0) return;

    const existing = this.cache.get(conversationId);
    if (!existing) return;

    const previousTitle = existing.title;
    const previousAutoFlag = existing.titleAutoGenerated;
    const newTitle = trimmed.slice(0, MANUAL_TITLE_MAX_CHARS);

    existing.title = newTitle;
    existing.titleAutoGenerated = false;
    existing.updatedAt = new Date().toISOString();
    this.emitConversations();

    if (!this.serverKnownIds.has(conversationId)) return;

    const body: RenameConversationRequest = { title: newTitle };
    this.http
      .patch<void>(`${this.conversationsBaseUrl}/${conversationId}`, body)
      .pipe(take(1), catchError((err: unknown) => {
        const current = this.cache.get(conversationId);
        if (current) {
          current.title = previousTitle;
          current.titleAutoGenerated = previousAutoFlag;
          this.emitConversations();
        }
        // eslint-disable-next-line no-console
        console.warn('[ChatService] renameConversation failed, reverted', err);
        return of(void 0);
      }))
      .subscribe();
  }

  /** Replaces a system-generated title with a short summary of the first user
   * prompt. Skips conversations the user already renamed manually. Persists
   * the title to the backend so it survives a tab reload. */
  maybeAutoTitle(conversationId: string, userPrompt: string): void {
    const existing = this.cache.get(conversationId);
    if (!existing) return;
    if (existing.titleAutoGenerated === false) return;

    const cleanPrompt = userPrompt.replace(/\s+/g, ' ').trim();
    if (cleanPrompt.length === 0) return;

    const summary = cleanPrompt.length > AUTO_TITLE_MAX_CHARS
      ? cleanPrompt.slice(0, AUTO_TITLE_MAX_CHARS).trimEnd() + '…'
      : cleanPrompt;

    existing.title = summary;
    // Stays auto-generated locally; the backend will mark it manual when it
    // accepts the PATCH but that flag is never read back from the server here
    // — the local one is authoritative for "should I overwrite this title?".
    existing.titleAutoGenerated = true;
    existing.updatedAt = new Date().toISOString();
    this.emitConversations();

    // Only patch once we know the row exists in DB. The first turn marks the
    // conversation as serverKnown, so the PATCH lands on the second turn at
    // the earliest — exactly when we want it (we have a real prompt to title).
    if (!this.serverKnownIds.has(conversationId)) return;

    const body: RenameConversationRequest = { title: summary };
    this.http
      .patch<void>(`${this.conversationsBaseUrl}/${conversationId}`, body)
      .pipe(take(1), catchError((err: unknown) => {
        // Title patch failure is non-blocking — the local title still works.
        // eslint-disable-next-line no-console
        console.warn('[ChatService] maybeAutoTitle PATCH failed (non-fatal)', err);
        return of(void 0);
      }))
      .subscribe();
  }

  appendMessage(conversationId: string, message: ChatMessage): void {
    const existing = this.cache.get(conversationId);
    if (!existing) return;
    existing.messages = [...existing.messages, message];
    existing.updatedAt = new Date().toISOString();

    // First user message on a local-only conversation: the backend will create
    // the row implicitly via EnsureConversationAsync. From here on PATCH/DELETE
    // are safe to call. (We can't tell user vs assistant reliably across tool
    // turns, so we mark as serverKnown as soon as ANY message lands — by then
    // the user-message-before-stream path has already hit the API.)
    if (message.role === 'user') {
      this.serverKnownIds.add(conversationId);
      // We have local mutations layered on top of whatever the server holds,
      // so the cached message list is no longer "fresh" from server — but it
      // is the most up-to-date view we have. Don't refetch on the next pick.
      this.messagesLoadedIds.add(conversationId);
    }

    this.emitConversations();
  }

  updateMessage(conversationId: string, messageId: string, mutate: (msg: ChatMessage) => ChatMessage): void {
    const existing = this.cache.get(conversationId);
    if (!existing) return;
    existing.messages = existing.messages.map((m) => (m.id === messageId ? mutate(m) : m));
    existing.updatedAt = new Date().toISOString();
    this.emitConversations();
  }

  /** Streams a chat turn from POST /api/chat/messages (text/event-stream).
   * HttpClient doesn't support SSE natively, so we use fetch + ReadableStream. */
  sendMessage(request: ChatSendRequest, abort: AbortSignal): Observable<ChatStreamEvent> {
    const subject = new Subject<ChatStreamEvent>();

    this.streamTurn(request, abort, subject).catch((err) => {
      if (abort.aborted) {
        subject.complete();
        return;
      }
      subject.next({
        type: 'error',
        errorCode: 'CHAT_NETWORK',
        message: err instanceof Error ? err.message : 'Network error',
        retryable: true,
      });
      subject.complete();
    });

    return subject.asObservable();
  }

  buildSendRequest(conversationId: string, sessionHint?: string): ChatSendRequest {
    const convo = this.getConversation(conversationId);
    const raw = (convo?.messages ?? [])
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .filter((m) => !m.isError)
      .filter((m) => (m.content != null && m.content.trim().length > 0)
                   || (m.attachments && m.attachments.some(a => a.base64Data || a.textContent)));

    // Collapse consecutive messages from the same role — stale user retries
    // left by failed turns would otherwise violate Anthropic's alternating
    // role constraint and bloat the prompt.
    const deduped: Array<{ role: 'user' | 'assistant'; content: string; attachments?: import('../models/chat.models').ChatAttachment[] }> = [];
    for (const m of raw) {
      const role = m.role as 'user' | 'assistant';
      const last = deduped[deduped.length - 1];
      if (last && last.role === role) {
        last.content = m.content;
        // Keep attachments from the latest message of collapsed duplicates
        if (m.attachments?.length) last.attachments = m.attachments;
      } else {
        deduped.push({ role, content: m.content, attachments: m.attachments });
      }
    }

    // For user messages that have text-based file attachments (CSV, Excel, TXT…):
    //   1. Append the file textContent to content (API-only — stored message stays clean)
    //   2. Strip text-type attachments from the attachments array (content already has the data;
    //      the backend DTO only accepts image/* and application/pdf as binary attachments)
    const messagesForApi = deduped.slice(-MAX_HISTORY_TURNS).map((m) => {
      if (m.role !== 'user') return m;

      const textFromFiles = (m.attachments ?? [])
        .filter(a => a.textContent)
        .map(a => a.textContent!)
        .join('\n');

      const apiContent = textFromFiles
        ? (m.content ? `${m.content}\n${textFromFiles}` : textFromFiles)
        : m.content;

      // Only binary attachments (images, PDFs) go to the backend
      const binaryAttachments = (m.attachments ?? []).filter(
        a => a.base64Data && (a.mediaType.startsWith('image/') || a.mediaType === 'application/pdf')
      );

      return {
        ...m,
        content: apiContent,
        attachments: binaryAttachments.length ? binaryAttachments : undefined,
      };
    });

    const convoObj = this.getConversation(conversationId);
    return {
      conversationId,
      messages: messagesForApi,
      sessionHint,
      agentCode: convoObj?.agentCode ?? this.currentAgentCode ?? undefined,
      language: undefined,
    };
  }

  randomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** GET /api/chat/usage — never errors with 404; backend returns zeros for
   * users with no row yet. Surfaced for any future header/badge that wants to
   * display token consumption. */
  getUsage(): Observable<ChatUsageResponse> {
    return this.http.get<ChatUsageResponse>(this.usageUrl);
  }

  // ---------------------------------------------------------------------------
  // Internal — server sync
  // ---------------------------------------------------------------------------

  private loadConversationsFromServer(): void {
    this.http
      .get<ChatConversationListItemDto[]>(`${this.conversationsBaseUrl}?page=1&pageSize=${LIST_PAGE_SIZE}`)
      .pipe(take(1), catchError((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[ChatService] loadConversations failed', err);
        return of([] as ChatConversationListItemDto[]);
      }))
      .subscribe((items) => {
        // Server is authoritative for the visible list, but DON'T blow away
        // unsaved local-only conversations (created by the user but not yet
        // sent to the backend) — they should keep showing up in the sidebar.
        const localOnly = Array.from(this.cache.values())
          .filter((c) => !this.serverKnownIds.has(c.id));

        this.cache.clear();
        // Preserved local-only entries first; the server list overrides if
        // an id collides (extremely unlikely with random UUIDs).
        for (const c of localOnly) this.cache.set(c.id, c);

        for (const dto of items) {
          this.serverKnownIds.add(dto.id);
          // Keep the existing in-memory entry if we already have it with
          // mutations layered on (an in-flight stream); otherwise hydrate
          // a fresh stub. Either way merge the latest server fields.
          const prior = this.cache.get(dto.id);
          this.cache.set(dto.id, prior
            ? { ...prior, title: dto.title, updatedAt: dto.updatedAt, titleAutoGenerated: dto.titleAutoGenerated, agentCode: dto.agentCode ?? undefined }
            : {
                id: dto.id,
                title: dto.title,
                updatedAt: dto.updatedAt,
                titleAutoGenerated: dto.titleAutoGenerated,
                agentCode: dto.agentCode ?? undefined,
                messages: [],
              });
        }

        this.emitConversations();
      });
  }

  /** Lazy load of a conversation's full message history — only fires for
   * server-known ids whose detail hasn't been fetched in this session. */
  private maybeFetchMessages(conversationId: string): void {
    if (!this.serverKnownIds.has(conversationId)) return;
    if (this.messagesLoadedIds.has(conversationId)) return;

    this.http
      .get<ChatConversationDetailDto>(`${this.conversationsBaseUrl}/${conversationId}`)
      .pipe(take(1), catchError((err: unknown) => {
        const status = this.statusOf(err);
        if (status === 404) {
          // Conversation was deleted in another tab while we were idle.
          this.cache.delete(conversationId);
          this.serverKnownIds.delete(conversationId);
          this.emitConversations();
          if (this.activeConversationIdSubject.value === conversationId) {
            const next = this.visibleSortedList()[0]?.id ?? null;
            this.activeConversationIdSubject.next(next);
          }
        }
        // eslint-disable-next-line no-console
        else console.warn('[ChatService] fetch detail failed', err);
        return of(null);
      }))
      .subscribe((detail) => {
        if (!detail) return;
        const existing = this.cache.get(conversationId);
        const merged: ChatConversation = {
          id: detail.id,
          title: detail.title,
          updatedAt: detail.updatedAt,
          titleAutoGenerated: detail.titleAutoGenerated,
          agentCode: detail.agentCode ?? undefined,
          messages: detail.messages.map((m) => this.toLocalMessage(m)),
        };
        // Keep the active in-flight assistant message if any (streaming text
        // may have already started against the local cache while we were
        // fetching). It's keyed by isStreaming flag.
        if (existing) {
          const inflight = existing.messages.filter((m) => m.isStreaming);
          if (inflight.length > 0) merged.messages = [...merged.messages, ...inflight];
        }
        this.cache.set(conversationId, merged);
        this.messagesLoadedIds.add(conversationId);
        this.emitConversations();
      });
  }

  private toLocalMessage(m: ChatBackendMessageDto): ChatMessage {
    return {
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: m.createdAt,
      agentCode: m.agentCode ?? undefined,
    };
  }

  // ---------------------------------------------------------------------------
  // Internal — emission helpers
  // ---------------------------------------------------------------------------

  private emitConversations(): void {
    this.conversationsSubject.next(this.visibleSortedList());
  }

  private visibleSortedList(): ChatConversation[] {
    const all = Array.from(this.cache.values());
    const filtered = this.currentAgentCode == null
      ? all
      : all.filter((c) => (c.agentCode ?? null) === this.currentAgentCode);
    return [...filtered].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  private statusOf(err: unknown): number {
    if (err && typeof err === 'object' && 'status' in err) {
      const s = (err as { status: unknown }).status;
      return typeof s === 'number' ? s : 0;
    }
    return 0;
  }

  // ---------------------------------------------------------------------------
  // Internal — SSE streaming (unchanged from sessionStorage version).
  // ---------------------------------------------------------------------------

  private async streamTurn(
    request: ChatSendRequest,
    abort: AbortSignal,
    subject: Subject<ChatStreamEvent>
  ): Promise<void> {
    const token = await this.acquireToken();
    const response = await fetch(`${environment.cafler_backoffice_back_url}/chat/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal: abort,
    });

    if (!response.ok) {
      let errorCode = 'CHAT_UPSTREAM';
      let message = `Request failed with status ${response.status}`;
      if (response.status === 404) {
        errorCode = 'CHAT_FEATURE_DISABLED';
        message = 'Chat AI is not enabled in this environment.';
      } else if (response.status === 503) {
        errorCode = 'CHAT_DPA_NOT_SIGNED';
        message = 'Chat AI is pending legal sign-off.';
      } else if (response.status === 429) {
        errorCode = 'CHAT_RATE_LIMITED';
        message = 'Too many messages. Please wait a moment.';
      } else if (response.status === 401) {
        errorCode = 'CHAT_UNAUTHENTICATED';
        message = 'Please sign in again.';
      }
      subject.next({ type: 'error', errorCode, message, retryable: response.status >= 500 });
      subject.complete();
      return;
    }

    if (!response.body) {
      subject.next({
        type: 'error',
        errorCode: 'CHAT_UPSTREAM',
        message: 'Empty stream from server.',
        retryable: true,
      });
      subject.complete();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      if (abort.aborted) {
        await reader.cancel().catch(() => undefined);
        break;
      }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const parsed = this.parseSseChunk(chunk);
        if (parsed) subject.next(parsed);
      }
    }

    subject.complete();
  }

  private parseSseChunk(chunk: string): ChatStreamEvent | null {
    let eventType: string | null = null;
    let dataRaw = '';
    for (const line of chunk.split('\n')) {
      if (line.startsWith('event: ')) eventType = line.slice(7).trim();
      else if (line.startsWith('data: ')) dataRaw += line.slice(6);
    }
    if (!eventType || !dataRaw) return null;
    try {
      const parsed = JSON.parse(dataRaw) as Record<string, unknown>;
      return { type: eventType as ChatStreamEvent['type'], ...parsed } as ChatStreamEvent;
    } catch {
      return null;
    }
  }

  private async acquireToken(): Promise<string | null> {
    // Reuse AuthService.getAccessToken() — the single source of truth for bearer
    // tokens in this app. It already handles scopes, deduplication and retries,
    // exactly the same way AuthInterceptor does for HttpClient requests. We
    // cannot use the interceptor here because fetch() bypasses Angular's
    // HttpClient pipeline, so we hit the same underlying service instead.
    try {
      const token = await this.authService.getAccessToken();
      return token ?? null;
    } catch {
      return null;
    }
  }
}
